import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

import { useSubscription } from '../../../contexts/SubscriptionContext';
import { accountService } from '../../../services/accountService';
import { subscriptionService } from '../../../services/subscriptionService';

const CreateAccountForm = ({ onClose, onSubmit }) => {
  const { canUse } = useSubscription();
  const [formData, setFormData] = useState({
    name: '',
    group: '',
    type: '',
    currency: 'BBD',
    openingBalance: '',
    creditLimit: '',
    principalAmount: '',
    startDate: '',
    loanTerm: ''
  });

  const [errors, setErrors] = useState({});
  const [accountTypeLimits, setAccountTypeLimits] = useState(null);
  const [existingAccounts, setExistingAccounts] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const groupOptions = [
    { value: 'Assets', label: 'Assets' },
    { value: 'Liabilities', label: 'Liabilities' },
    { value: 'Cash', label: 'Cash' }
  ];

  useEffect(() => {
    loadAccountTypeLimits();
    loadExistingAccounts();
  }, []);

  const loadAccountTypeLimits = async () => {
    try {
      const limits = await subscriptionService?.getAccountTypeLimits();
      setAccountTypeLimits(limits);
    } catch (error) {
      console.error('Error loading account type limits:', error);
    }
  };

  const loadExistingAccounts = async () => {
    try {
      const accounts = await accountService?.getAll();
      setExistingAccounts(accounts || []);
    } catch (error) {
      console.error('Error loading existing accounts:', error);
    }
  };

  const getAccountTypeCount = (type) => {
    return existingAccounts?.filter(acc => acc?.type === type)?.length || 0;
  };

  const isAccountTypeDisabled = (type) => {
    if (!accountTypeLimits || accountTypeLimits?.unlimited) return false;
    
    const limits = accountTypeLimits?.limits;
    if (!limits) return false;

    const currentCount = getAccountTypeCount(type);
    const limit = limits?.[type];
    
    return limit !== undefined && currentCount >= limit;
  };

  const getAccountTypeLabel = (type, label) => {
    if (!accountTypeLimits || accountTypeLimits?.unlimited) return label;
    
    const limits = accountTypeLimits?.limits;
    if (!limits) return label;

    const currentCount = getAccountTypeCount(type);
    const limit = limits?.[type];
    
    if (limit !== undefined) {
      const remaining = Math.max(0, limit - currentCount);
      if (remaining === 0) {
        return `${label} (Limit reached - Upgrade to Pro)`;
      }
      return `${label} (${remaining} remaining)`;
    }
    
    return label;
  };

  const typeOptions = [
    { 
      value: 'Chequing', 
      label: getAccountTypeLabel('Chequing', 'Chequing Account'),
      disabled: isAccountTypeDisabled('Chequing'),
      group: 'Assets'
    },
    { 
      value: 'Savings', 
      label: getAccountTypeLabel('Savings', 'Savings Account'),
      disabled: isAccountTypeDisabled('Savings'),
      group: 'Assets'
    },
    { 
      value: 'Investment', 
      label: getAccountTypeLabel('Investment', 'Investment Account'),
      disabled: isAccountTypeDisabled('Investment'),
      group: 'Assets'
    },
    { 
      value: 'Cash', 
      label: getAccountTypeLabel('Cash', 'Cash'),
      disabled: isAccountTypeDisabled('Cash'),
      group: 'Cash'
    },
    { 
      value: 'Credit Card', 
      label: getAccountTypeLabel('Credit Card', 'Credit Card'),
      disabled: isAccountTypeDisabled('Credit Card'),
      group: 'Liabilities'
    },
    { 
      value: 'Line of Credit', 
      label: getAccountTypeLabel('Line of Credit', 'Line of Credit'),
      disabled: isAccountTypeDisabled('Line of Credit'),
      group: 'Liabilities'
    },
    { 
      value: 'Loan', 
      label: getAccountTypeLabel('Loan', 'Loan'),
      disabled: isAccountTypeDisabled('Loan'),
      group: 'Liabilities'
    },
    { 
      value: 'Collections', 
      label: getAccountTypeLabel('Collections', 'Collections'),
      disabled: isAccountTypeDisabled('Collections'),
      group: 'Liabilities'
    }
  ];

  // Filter type options based on selected group
  const filteredTypeOptions = formData?.group 
    ? typeOptions?.filter(option => option?.group === formData?.group)
    : typeOptions;

  const currencyOptions = [
    { value: 'USD', label: 'USD - US Dollar' },
    { value: 'EUR', label: 'EUR - Euro' },
    { value: 'GBP', label: 'GBP - British Pound' },
    { value: 'CAD', label: 'CAD - Canadian Dollar' },
    { value: 'AUD', label: 'AUD - Australian Dollar' },
    { value: 'JPY', label: 'JPY - Japanese Yen' },
    { value: 'INR', label: 'INR - Indian Rupee' },
    { value: 'BBD', label: 'BBD - Barbados Dollar' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Account name is required';
    }

    if (!formData?.group) {
      newErrors.group = 'Please select an account group';
    }

    if (!formData?.type) {
      newErrors.type = 'Please select an account type';
    }

    if (!formData?.openingBalance?.trim()) {
      newErrors.openingBalance = 'Opening balance is required';
    } else if (isNaN(parseFloat(formData?.openingBalance))) {
      newErrors.openingBalance = 'Please enter a valid number';
    }

    // Validate credit limit for Line of Credit and Credit Card
    if ((formData?.type === 'Line of Credit' || formData?.type === 'Credit Card') && formData?.creditLimit?.trim()) {
      if (isNaN(parseFloat(formData?.creditLimit))) {
        newErrors.creditLimit = 'Please enter a valid number';
      } else if (parseFloat(formData?.creditLimit) < 0) {
        newErrors.creditLimit = 'Credit limit must be positive';
      }
    }

    // Validate loan fields for Loan accounts
    if (formData?.type === 'Loan') {
      if (!formData?.principalAmount?.trim()) {
        newErrors.principalAmount = 'Principal amount is required for loan accounts';
      } else if (isNaN(parseFloat(formData?.principalAmount)) || parseFloat(formData?.principalAmount) <= 0) {
        newErrors.principalAmount = 'Please enter a valid positive amount';
      }

      if (!formData?.startDate) {
        newErrors.startDate = 'Start date is required for loan accounts';
      }

      if (!formData?.loanTerm?.trim()) {
        newErrors.loanTerm = 'Loan term is required for loan accounts';
      } else if (isNaN(parseInt(formData?.loanTerm)) || parseInt(formData?.loanTerm) <= 0) {
        newErrors.loanTerm = 'Please enter a valid loan term in months';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      try {
        const accountData = {
          name: formData?.name,
          type: formData?.type,
          group: formData?.group,
          balance: parseFloat(formData?.openingBalance),
          currency: formData?.currency
        };

        // Add credit limit if provided for Line of Credit or Credit Card
        if ((formData?.type === 'Line of Credit' || formData?.type === 'Credit Card') && formData?.creditLimit?.trim()) {
          accountData.creditLimit = parseFloat(formData?.creditLimit);
        }

        // Add loan details if Loan account
        if (formData?.type === 'Loan') {
          accountData.principalAmount = parseFloat(formData?.principalAmount);
          accountData.startDate = formData?.startDate;
          accountData.loanTerm = parseInt(formData?.loanTerm);
        }

        await onSubmit(accountData);
      } catch (error) {
        console.error('Error creating account:', error);
        setErrors({ submit: error?.message || 'Failed to create account. Please try again.' });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-16 md:p-24 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-elevation-5 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-20 md:p-24 flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
              <Icon name="Plus" size={24} color="var(--color-primary)" strokeWidth={2.5} />
            </div>
            <h2 className="ml-12 text-xl md:text-2xl font-heading font-semibold text-card-foreground">
              Create New Account
            </h2>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-muted transition-smooth"
            aria-label="Close form"
          >
            <Icon name="X" size={24} color="var(--color-foreground)" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-20 md:p-24 space-y-20 md:space-y-24">
          <Input
            label="Account Name"
            type="text"
            placeholder="e.g., Main Checking Account"
            value={formData?.name}
            onChange={(e) => handleChange('name', e?.target?.value)}
            error={errors?.name}
            required
            description="Choose a descriptive name for easy identification"
          />

          <Select
            label="Account Group"
            placeholder="Select account group"
            options={groupOptions}
            value={formData?.group}
            onChange={(value) => handleChange('group', value)}
            error={errors?.group}
            required
            description="Categorize your account for better organization"
          />

          <Select
            label="Account Type"
            placeholder="Select account type"
            options={filteredTypeOptions}
            value={formData?.type}
            onChange={(value) => handleChange('type', value)}
            error={errors?.type}
            required
            description={formData?.group ? `Showing ${formData?.group} account types only` : 'Select an account group first to filter types'}
          />

          {errors?.submit && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-12">
              <p className="text-sm text-error font-caption">{errors?.submit}</p>
            </div>
          )}

          <Input
            label="Opening Balance"
            type="number"
            placeholder="0.00"
            value={formData?.openingBalance}
            onChange={(e) => handleChange('openingBalance', e?.target?.value)}
            error={errors?.openingBalance}
            required
            description="Enter the current balance of this account"
          />

          {(formData?.type === 'Line of Credit' || formData?.type === 'Credit Card') && (
            <Input
              label="Credit Limit (Optional)"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={formData?.creditLimit}
              onChange={(e) => handleChange('creditLimit', e?.target?.value)}
              error={errors?.creditLimit}
              description="Enter the maximum credit limit for this account"
            />
          )}

          {formData?.type === 'Loan' && (
            <>
              <Input
                label="Principal Amount *"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData?.principalAmount}
                onChange={(e) => handleChange('principalAmount', e?.target?.value)}
                error={errors?.principalAmount}
                description="Original loan amount"
                required
              />
              <Input
                label="Start Date *"
                type="date"
                value={formData?.startDate}
                onChange={(e) => handleChange('startDate', e?.target?.value)}
                error={errors?.startDate}
                description="Date when the loan started"
                required
              />
              <Input
                label="Loan Term (Months) *"
                type="number"
                placeholder="e.g., 60 for 5 years"
                value={formData?.loanTerm}
                onChange={(e) => handleChange('loanTerm', e?.target?.value)}
                error={errors?.loanTerm}
                description="Total loan duration in months"
                required
              />
            </>
          )}

          <Select
            label="Currency"
            options={currencyOptions}
            value={formData?.currency}
            onChange={(e) => handleChange('currency', e?.target?.value)}
            required
          />

          <div className="flex gap-12 pt-20 border-t border-border">
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="w-full"
              iconName="Check"
              iconPosition="left"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating Account...' : 'Create Account'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAccountForm;